import type { PostParent, PostTypes } from '$lib/api/restApi';
import type { HubsAPI, Post, presenterShortCollection } from '$lib/api/rpc';
import type { BlockType } from '$lib/components/TextEditor/Components/Popovers/InsertBlock/InsertBlock.svelte';
import type { Collection } from '$lib/types/Collections';
import { get, writable, type Writable } from 'svelte/store';

export const isComposerOpen = writable(false);

export const composerInitBlock: Writable<BlockType | null> = writable(null);

export const createEmptyPost = (widget = false): PostParent => {
	const post = {
		id: null,
		status: undefined,
		type: (widget ? 'widgetQuickPoll' : 'post') as PostTypes,
		children: [],
		title_image: {
			url: '',
			width: 0,
			height: 0,
			cloudinary: false,
		},
		collection_id: undefined,
		context: '',
		title: '',
		hide_all: false,
		description: undefined,
		published_at: undefined,
		collection: undefined,
		tags: [],
	} as PostParent;
	if (widget) {
		post.etc = {
			widget: {
				url: '',
				html: '',
			},
		};
	}
	return post;
};

export let composerPost: Writable<PostParent> = writable(createEmptyPost());

export const openComposer = ({
	collection,
	initBlock,
}: {
	collection?: Collection | presenterShortCollection;
	initBlock?: BlockType;
}) => {
	const post = createEmptyPost();
	if (collection) post.collection = collection;
	if (initBlock) composerInitBlock.set(initBlock);
	composerPost.set(post);
	isComposerOpen.set(true);
};

export const editPost = async (postStore: Writable<Post>, api?: HubsAPI) => {
	const post = get(postStore);
	if (post.children.length == 0 && api) {
		const resp = await api.getPost({ hubId: post.hub_id, postId: post.id });
		if (!resp.post.title_image) {
			resp.post.title_image = {
				url: '',
				width: 0,
				height: 0,
				cloudinary: false,
			};
		}
		postStore.set(resp.post);
	}
	composerPost = postStore as Writable<PostParent>;
	isComposerOpen.set(true);
};

export const previewImages: Writable<string[]> = writable([]);

export const addPreviewImages = (images: string[]) => {
	const isNewPost = get(composerPost).id === null;

	previewImages.update((prev) => [...prev, ...new Set(images)]);
	composerPost.update((post) => {
		if (post.title_image.url) return post;
		return {
			...post,
			title_image: {
				...post.title_image,
				// Only infer the title image if it's a new post
				url: isNewPost ? images[0] ?? '' : '',
			},
		};
	});
};

export const removePreviewImages = (images: string[]) => {
	previewImages.update((prevImages) => {
		const newImages = prevImages.filter((prevImage) => !images.includes(prevImage));
		composerPost.update((post) => {
			if (images.includes(post.title_image?.url))
				return {
					...post,
					title_image: {
						...post.title_image,
						url: newImages.length ? newImages[0] : '',
					},
				};
			return post;
		});
		return newImages;
	});
};

export const unsetComposerStores = () => {
	isComposerOpen.set(false);
	composerInitBlock.set(null);
	previewImages.set([]);
};
